import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { expenseService, expenseCategoryService, Expense, ExpenseCategory } from '@/lib/storage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Wallet,
  Send,
  Loader2,
  CheckCircle2,
  IndianRupee,
  Pencil,
  Building,
  CreditCard,
  Building2,
  Hash,
} from 'lucide-react';

interface ExpenseModalProps {
  open: boolean;
  onClose: () => void;
  expense?: Expense | null;
}

export default function ExpenseModal({ open, onClose, expense }: ExpenseModalProps) {
  const { session } = useAuth();
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [category, setCategory] = useState('Other Exp');
  const [clients, setClients] = useState<string[]>([]);
  const [clientName, setClientName] = useState('');
  const [isGst, setIsGst] = useState(false);
  const [bank, setBank] = useState('HDFC');
  const [paymentMethod, setPaymentMethod] = useState('GPay');
  const [rfNo, setRfNo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const isEditing = !!expense;

  const selectedCatObj = categories.find(c => c.name === category);
  const isSpecial = !!(selectedCatObj?.isSpecial);

  useEffect(() => {
    if (open) {
      expenseCategoryService.getAll()
        .then(res => setCategories(res))
        .catch(err => console.error('Failed to fetch categories:', err));
      expenseService.getExternalClients()
        .then(res => setClients(res))
        .catch(err => console.error('Failed to fetch clients:', err));
    }
  }, [open]);

  useEffect(() => {
    if (expense) {
      setAmount(String(expense.amount));
      setRemark(expense.remark || '');
      setCategory(expense.category || 'Other Exp');
      setClientName(expense.clientName || '');
      setIsGst(!!expense.isGst);
      setBank(expense.bank || 'HDFC');
      setPaymentMethod(expense.paymentMethod || 'GPay');
      setRfNo(expense.rfNo || '');
    } else {
      setAmount('');
      setRemark('');
      setCategory('Other Exp');
      setClientName('');
      setIsGst(false);
      setBank('HDFC');
      setPaymentMethod('GPay');
      setRfNo('');
    }
    setSubmitted(false);
    setError('');
  }, [expense, open]);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (isSpecial && !clientName) {
      setError('Please select a client for this special category expense');
      return;
    }
    if (!session?.userId) return;

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        amount: parseFloat(amount),
        remark: remark.trim() || undefined,
        category,
        clientName: isSpecial ? clientName : undefined,
        isGst: isSpecial ? isGst : undefined,
        bank: isSpecial ? bank : undefined,
        paymentMethod: isSpecial ? paymentMethod : undefined,
        rfNo: isSpecial ? rfNo : undefined,
      };

      if (isEditing && expense) {
        await expenseService.update(expense.id, payload);
      } else {
        await expenseService.create({
          staffId: session.userId,
          staffName: session.name,
          ...payload,
        });
      }
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit expense. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setRemark('');
    setCategory('Other Exp');
    setClientName('');
    setIsGst(false);
    setBank('HDFC');
    setPaymentMethod('GPay');
    setRfNo('');
    setSubmitted(false);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {isEditing ? <Pencil className="w-5 h-5 text-indigo-500" /> : <Wallet className="w-5 h-5 text-indigo-500" />}
            {isEditing ? 'Edit Expense' : 'Add Daily Expense'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEditing ? 'Update your pending expense.' : 'Submit your expense for today for admin approval.'}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-base">
                {isEditing ? 'Expense Updated!' : 'Expense Submitted!'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {isEditing
                  ? 'Your expense has been updated.'
                  : `Your expense of ₹${parseFloat(amount).toFixed(2)} has been sent for approval.`}
              </p>
            </div>
            <Button className="w-full mt-2" onClick={handleClose}>Done</Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Category selection */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">Category</Label>
              <Select value={category} onValueChange={(val) => { setCategory(val); setError(''); }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      {c.name} {c.isSpecial ? '★' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Special category form fields */}
            {isSpecial && (
              <div className="p-4 rounded-2xl bg-indigo-50/50 dark:bg-slate-900/50 border border-indigo-100/50 dark:border-slate-800 space-y-4 animate-in fade-in slide-in-from-top-3 duration-200">
                <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 tracking-wider uppercase">
                  Special Category Fields
                </p>

                {/* Client Selection */}
                <div className="space-y-2">
                  <Label htmlFor="clientName" className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                    <Building className="w-3.5 h-3.5 text-slate-400" /> Client Name *
                  </Label>
                  <Select value={clientName} onValueChange={(val) => { setClientName(val); setError(''); }}>
                    <SelectTrigger className="w-full bg-white dark:bg-slate-950">
                      <SelectValue placeholder="Select Client" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {clients.map((c, idx) => (
                        <SelectItem key={idx} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Payment Method */}
                  <div className="space-y-2">
                    <Label htmlFor="paymentMethod" className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-slate-400" /> Payment Method
                    </Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="w-full bg-white dark:bg-slate-950">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GPay">GPay</SelectItem>
                        <SelectItem value="Online">Online</SelectItem>
                        <SelectItem value="NEFT">NEFT</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bank */}
                  <div className="space-y-2">
                    <Label htmlFor="bank" className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" /> Bank Account
                    </Label>
                    <Select value={bank} onValueChange={setBank}>
                      <SelectTrigger className="w-full bg-white dark:bg-slate-950">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HDFC">HDFC</SelectItem>
                        <SelectItem value="SBI">SBI</SelectItem>
                        <SelectItem value="ICICI">ICICI</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-3 justify-between pt-1">
                  {/* GST Checkbox */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="gst"
                      checked={isGst}
                      onCheckedChange={(checked) => setIsGst(!!checked)}
                      className="border-slate-300 rounded-md animate-none"
                    />
                    <Label htmlFor="gst" className="text-xs font-semibold text-slate-600 dark:text-slate-450 cursor-pointer select-none">
                      Includes 18% GST
                    </Label>
                  </div>

                  {/* Reference Number */}
                  <div className="w-[60%] flex items-center gap-2 bg-white dark:bg-slate-950 rounded-xl px-2.5 py-1 border border-slate-200 dark:border-slate-800">
                    <Hash className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <Input
                      placeholder="Ref / Bill No."
                      value={rfNo}
                      onChange={(e) => setRfNo(e.target.value)}
                      className="border-none bg-transparent focus-visible:ring-0 p-0 text-xs h-7"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* General Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-sm font-medium">Amount (₹)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(''); }}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Remark */}
            <div className="space-y-2">
              <Label htmlFor="remark" className="text-sm font-medium">Remark (optional)</Label>
              <Textarea
                id="remark"
                placeholder="What is this expense for?"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={3}
              />
            </div>

            {error && (
              <p className="text-xs text-red-600 dark:text-red-400 font-semibold">{error}</p>
            )}

            <Button
              className="w-full gap-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl h-11"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {submitting ? 'Saving...' : isEditing ? 'Update Expense' : 'Submit for Approval'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
